/**
 * Crew videos slice — the writable third of the media carousel.
 *
 * Its own file rather than a corner of `list.ts` for one reason: a video hangs
 * off a GAME, not off a list entry. A member can attach a clip to anything they
 * can open a Detail screen for, including a live-search result that is not on
 * the shared list at all, so keying this state off `ListEntry` would have made
 * the common case (`videos` for a game with no entry) the awkward one.
 *
 * The other two sources — Steam trailers and screenshots — are read-only and
 * live on the steamFacts slice, because they arrive with the appdetails fetch
 * and are never persisted (decision D2). `selectors.ts#mediaVM` is where the
 * three meet.
 */

import type { StateCreator } from 'zustand';

import type { GameId, GameVideo } from '../../lib/types.ts';
import { parseVideoUrl, VIDEO_TITLE_MAX } from '../../lib/videoUrl.ts';
import { mutate, storeApi } from '../mutate.ts';
import type { MutateOpts } from '../mutate.ts';
import type { QdStore } from '../types.ts';

export const VIDEO_BAD_URL_TOAST = '✕ NOT A YOUTUBE OR TWITCH LINK';
export const VIDEO_DUPE_TOAST = '✕ ALREADY ADDED';
export const VIDEO_ADDED_TOAST = 'VIDEO ADDED';
export const VIDEO_REMOVED_TOAST = 'VIDEO REMOVED';

export interface VideosSlice {
  /** Every crew video for every game — hydrated whole from the boot snapshot. */
  videos: GameVideo[];

  addVideo: (gameId: GameId, rawUrl: string, title: string) => Promise<void>;
  rmVideo: (videoId: string) => Promise<void>;
}

/**
 * `video:<appid>:<provider>:<key>` / `video:rm:<id>` — must match
 * `patchEchoKey` exactly, INCLUDING the provider segment. Migration 3006 made
 * (provider, key) the identity; keying the echo on the key alone would let a
 * Twitch clip whose slug matches a YouTube id swallow the wrong echo.
 */
export const videoAddKey = (appid: number, provider: string, key: string): string =>
  'video:' + appid + ':' + provider + ':' + key;
export const videoRmKey = (videoId: string): string => 'video:rm:' + videoId;

export const createVideosSlice: StateCreator<QdStore, [], [], VideosSlice> = (set, get) => ({
  videos: [],

  addVideo: async (gameId, rawUrl, title) => {
    const s = get();
    const m = s.me();
    const g = s.gById(gameId);
    if (!m || !g) return;

    // Validation happens BEFORE `mutate()`: the pipeline's error toast is for a
    // commit that failed, and a link that never was a link has nothing to
    // optimistically apply and nothing to roll back.
    const parsed = parseVideoUrl(rawUrl);
    if (!parsed) {
      s.toast(VIDEO_BAD_URL_TOAST, true);
      return;
    }
    if (!g.appid) {
      // D-02: a CheapShark-only row has no appid the FK can hold. `mediaVM`
      // already refuses to show the composer here; this is the belt.
      s.toast(VIDEO_BAD_URL_TOAST, true);
      return;
    }
    // The same clip twice is the unique index's job, but catching it locally
    // saves a round trip — same shape as `quickAdd`'s ALREADY ON THE LIST.
    // Compared on PROVIDER TOO, matching the unique index. A Twitch clip slug
    // may legally be 11 URL-safe characters, so key-only would report ALREADY
    // ADDED for a link the database would happily accept.
    if (
      s.videos.some(
        (v) => v.appid === g.appid && v.provider === parsed.provider && v.key === parsed.key,
      )
    ) {
      s.toast(VIDEO_DUPE_TOAST, true);
      return;
    }

    const draft = {
      provider: parsed.provider,
      kind: parsed.kind,
      key: parsed.key,
      url: parsed.url,
      title: title.trim().slice(0, VIDEO_TITLE_MAX),
    };
    const optimistic: GameVideo = {
      id: 'pending_' + Date.now(),
      appid: g.appid,
      ...draft,
      by: m.k,
      at: Date.now(),
    };

    const opts: MutateOpts<GameVideo> = {
      key: videoAddKey(g.appid, parsed.provider, parsed.key),
      optimistic: (st) => ({ videos: [...st.videos, optimistic] }),
      commit: async () => {
        try {
          return await s.repo.addGameVideo(g, draft);
        } catch (e) {
          // `mutate()` reads `rollbackToast` only after a commit has already
          // failed, so sharpening it here is in time. A duplicate is the one
          // failure that is NOT "try again" — somebody beat us to that clip
          // between the local check above and the insert.
          if ((e as { code?: unknown } | null)?.code === 'DUPLICATE') {
            opts.rollbackToast = VIDEO_DUPE_TOAST;
          }
          throw e;
        }
      },
      toast: VIDEO_ADDED_TOAST,
    };

    const created = await mutate(storeApi(get, set), opts);
    // Swap the placeholder for the server row so `rmVideo` addresses a real id.
    if (created) {
      set((st) => ({ videos: st.videos.map((v) => (v.id === optimistic.id ? created : v)) }));
    }
  },

  rmVideo: async (videoId) => {
    const s = get();
    if (!s.videos.some((v) => v.id === videoId)) return;
    await mutate(storeApi(get, set), {
      key: videoRmKey(videoId),
      optimistic: (st) => ({ videos: st.videos.filter((v) => v.id !== videoId) }),
      commit: () => s.repo.removeGameVideo(videoId),
      toast: VIDEO_REMOVED_TOAST,
    });
  },
});
